import React, {useMemo} from 'react'

import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {ContentfulFormContext} from '@github-ui/swp-core/components/contentful/ContentfulForm'
import {OctocaptchaContext} from '@github-ui/swp-core/components/forms/OctocaptchaContext'
import {ConsentExperienceContext} from '@github-ui/swp-core/components/forms/ConsentExperienceContext'
import {useConsentExperience} from '@github-ui/swp-core/components/forms/useConsentExperience'

import type {Country} from '@github-ui/swp-core/components/forms/ConsentExperience/types'

/**
 * The App component is used to render content which should be present on _all_ routes within this app
 */
export function App(props: {children?: React.ReactNode}) {
  /**
   * The app payload may be null depending on how the application is initialized
   * on the Rails controller.
   */
  const payload = useAppPayload<{
    marketingFormsApiHost?: string
    octocaptchaHost?: string
    datadomeCaptchaEnabled?: boolean
    marketingTargetedCountries: Country[]
    locale?: string
  } | null>()

  const locale = payload?.locale
  const datadomeCaptchaEnabled = payload?.datadomeCaptchaEnabled
  const octocaptchaCtx = useMemo(
    () => ({hostName: payload?.octocaptchaHost ?? '', originPage: 'marketing_forms', locale, datadomeCaptchaEnabled}),
    [payload?.octocaptchaHost, locale, datadomeCaptchaEnabled],
  )

  const contentfulFormCtx = useMemo(
    () => ({marketingFormsApiHost: payload?.marketingFormsApiHost}),
    [payload?.marketingFormsApiHost],
  )

  const consent = useConsentExperience()
  const consentExperienceCtx = useMemo(
    () => ({
      marketingTargetedCountries: payload?.marketingTargetedCountries ?? [],
      selectedCountry: consent.selectedCountry,
      setSelectedCountry: consent.setSelectedCountry,
    }),
    [payload?.marketingTargetedCountries, consent.selectedCountry, consent.setSelectedCountry],
  )

  return (
    <ErrorBoundary>
      <ContentfulFormContext value={contentfulFormCtx}>
        <OctocaptchaContext value={octocaptchaCtx}>
          <ConsentExperienceContext value={consentExperienceCtx}>{props.children}</ConsentExperienceContext>
        </OctocaptchaContext>
      </ContentfulFormContext>
    </ErrorBoundary>
  )
}
